/**
 * Account Membership — manages multi-user firm accounts.
 *
 * Firestore paths:
 *   accounts/{accountId}/members/{userId}   — MemberRecord
 *   users/{userId}/account_membership/main  — AccountMembership  (back-pointer)
 *
 * Phase 1: used to seed owner records for Fernando and the testing account so
 * that resolveAccount() can traverse the membership path in future.
 */

import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  getDocsFromServer,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { ACCOUNT_FIRESTORE_PREFIX } from './constants';
import type { MemberRecord, AccountMembership } from './types';
import { readDisplayName } from './memberDisplayName';

// ─────────────────────────────────────────────────────────────────────────────
// Write helpers — NONE. This module is read-only (D-1, Miles 2026-09-12).
//
// `addMemberToAccount` lived here and wrote accounts/{accountId}/members/{userId}
// from the BROWSER with `setDoc(..., { merge: true })`. It had no call sites —
// only a barrel re-export — but firestore.rules allowed an owner or admin that
// write, so it was a live path around every guard in functions/src/rbac
// (GUARD-1, GUARD-2, the three delegation guards, and now the escalation
// ceiling). The rule is server-only as of the same change; this is deleted so
// nothing reaches for it again and finds a permission-denied it does not
// understand. Every member record is written by the Admin SDK: rbacAssignRoles,
// rbacAcceptInvitation, recompute, founderBootstrap and the maintenance sweeps.
// Git history is the archive (D15).
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Read helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns all member records for an account.
 *
 * `fromServer` forces a server read (`getDocsFromServer`) that bypasses the
 * Firestore offline/IndexedDB cache — used by the add-people picker so a member
 * just seeded from another client (or the console) shows immediately with its
 * full, current fields (e.g. `displayName`) rather than a stale cached snapshot.
 * Falls back to the cache-tolerant read if the server read fails (offline).
 *
 * Each record's `userId` is normalized to the stored field OR the doc id, so a
 * member never surfaces without an identity (the picker/add flow keys on it).
 *
 * `strict` rethrows instead of returning `[]`. Swallowing the error is the right
 * default for label/avatar callers (a missing name degrades gracefully), but it
 * is WRONG for the add-people picker: a rules denial or an offline read then
 * renders as "No one else in this workspace to add", which is a lie the user
 * cannot act on. Those callers pass `strict` and surface a retry instead.
 */
export async function getAccountMembers(
  accountId: string,
  opts?: { fromServer?: boolean; strict?: boolean },
): Promise<MemberRecord[]> {
  try {
    const db = getFirestore(getApp());
    const membersRef = collection(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'members');
    let snap;
    if (opts?.fromServer) {
      try {
        snap = await getDocsFromServer(membersRef);
      } catch {
        snap = await getDocs(membersRef);
      }
    } else {
      snap = await getDocs(membersRef);
    }
    return snap.docs.map((d) => {
      const data = (d.data() ?? {}) as MemberRecord;
      const raw = data as unknown as Record<string, unknown>;
      const userId = String(data.userId ?? '').trim() || d.id;
      const displayName = readDisplayName(raw);
      // Normalize identity + display label so the picker never surfaces a member
      // without a uid, nor mislabels one whose displayName field was mis-keyed.
      return { ...data, userId, ...(displayName ? { displayName } : {}) };
    });
  } catch (err) {
    console.warn('[Prest0nRuntime] getAccountMembers failed:', err);
    if (opts?.strict) throw err;
    return [];
  }
}

/**
 * Returns the account membership record for a given user, or null if none exists.
 */
export async function getUserAccountMembership(userId: string): Promise<AccountMembership | null> {
  try {
    const db = getFirestore(getApp());
    const membershipRef = doc(db, 'users', userId, 'account_membership', 'main');
    const snap = await getDoc(membershipRef);
    if (!snap.exists()) return null;
    return snap.data() as AccountMembership;
  } catch (err) {
    console.warn('[Prest0nRuntime] getUserAccountMembership failed:', err);
    return null;
  }
}

/**
 * Retrieves a single member record from an account.
 */
export async function getAccountMember(
  accountId: string,
  userId: string
): Promise<MemberRecord | null> {
  try {
    const db = getFirestore(getApp());
    const memberRef = doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'members', userId);
    const snap = await getDoc(memberRef);
    if (!snap.exists()) return null;
    return snap.data() as MemberRecord;
  } catch (err) {
    console.warn('[Prest0nRuntime] getAccountMember failed:', err);
    return null;
  }
}
